import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import type { StepIconProps } from '@mui/material/StepIcon';
import StepLabel, { stepLabelClasses } from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ONBOARDING_PROGRESS_STEPS, getProgressStepIndex } from '../steps';
import type { OnboardingStepId } from '../types';

export type OnboardingStepperProps = {
    currentStep: OnboardingStepId;
};

const OnboardingConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 12,
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderTopWidth: 2,
        borderColor: theme.palette.divider,
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}, &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
        {
            borderColor: theme.palette.primary.main,
        },
}));

export function OnboardingStepIcon({ active, completed }: Pick<StepIconProps, 'active' | 'completed'>): ReactElement {
    if (completed) {
        return <CheckCircleIcon sx={{ fontSize: 24, color: 'primary.main' }} />;
    }
    if (active) {
        return <RadioButtonCheckedIcon sx={{ fontSize: 24, color: 'primary.main' }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ fontSize: 24, color: 'text.disabled' }} />;
}

export function OnboardingStepper({ currentStep }: OnboardingStepperProps): ReactElement {
    const { t } = useTranslation();
    const activeStep = getProgressStepIndex(currentStep);

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 2 }}>
            <Stepper
                activeStep={activeStep}
                alternativeLabel
                connector={<OnboardingConnector />}
                sx={{
                    [`& .${stepLabelClasses.label}.${stepLabelClasses.active}`]: {
                        color: 'primary.main',
                        fontWeight: 600,
                    },
                    [`& .${stepLabelClasses.label}.${stepLabelClasses.completed}`]: {
                        color: 'primary.main',
                    },
                }}>
                {ONBOARDING_PROGRESS_STEPS.map((step, index) => {
                    const label = <StepLabel slots={{ stepIcon: OnboardingStepIcon }}>{t(step.labelKey)}</StepLabel>;
                    const isCompleted = index < activeStep;
                    return (
                        <Step key={step.id} completed={isCompleted}>
                            {isCompleted ? (
                                <StepButton component={Link} to={step.path}>
                                    {label}
                                </StepButton>
                            ) : (
                                label
                            )}
                        </Step>
                    );
                })}
            </Stepper>
        </Box>
    );
}
